import { useCallback } from 'react';
import { useGlobalUrlParams } from './useGlobalUrlParams';

const decodeBase64Json = (base64: string) => JSON.parse(atob(base64)) as Record<string, string>;
const encodeBase64Json = (json: Record<string, unknown>) => btoa(JSON.stringify(json));

const EMPTY_PARAMS = {};

/**
 * A hook for interacting with widget specific params meant to be stored in the url
 * These params would concern UI states and are only manipulated within the browser
 * For any identifying params that the server is sending, use `useGlobalUrlParams` to interact.
 *
 *
 * @param identifier identifier for the scope that would share the same params.
 * e.g.
 * - using contentKey means only the specific content uses these params, even if there are other widgets
 * of the same type but are different content items
 * - using widget's name (i.e. MembershipContent) means all widgets with that name share params
 * - using a custom string in different widgets to make them use the same params
 * - using reactId should be avoided, since it is generated per page view thus changes all the time
 * @returns a tuple of [value, setter] much like what is returned from `useState()`.
 */
export const useWidgetUrlParams = <T extends Record<string, string>>(
  identifier: string
): [Partial<T>, (val: Partial<Record<keyof T, unknown>>) => void] => {
  const get = useCallback(
    (urlParams: URLSearchParams) => {
      const widgetParams = urlParams.get(identifier);
      return widgetParams ? (decodeBase64Json(widgetParams) as T) : EMPTY_PARAMS;
    },
    [identifier]
  );

  const update = useCallback(
    (urlParams: URLSearchParams, val: Partial<Record<keyof T, unknown>>) => {
      const base64Val = encodeBase64Json(val);
      urlParams.set(identifier, base64Val);
      return urlParams;
    },
    [identifier]
  );

  return useGlobalUrlParams<Partial<T>, Record<keyof T, unknown>>(get, update);
};
